import React from 'react';
import { CardContent, CardMedia, useTheme } from '@mui/material';
import { StyledCard } from './blogCard.styles';
import { blogCardDefaultProps, blogCardPropTypes } from './blogCard.propTypes';
import TypographyBlock from '../../typography_block/typographyblock';

/**
 * BlogCard component for displaying a blog post preview.
 * This component includes an image, title, content, and an optional action.
 * It is styled using Material-UI components and supports custom elevation and background color.
 *
 * @component
 * @example
 * const image = 'https://example.com/image.jpg';
 * const imageAlt = 'Blog Image';
 * const title = 'Blog Title';
 * const content = 'Brief description of the blog post content.';
 * const elevation = 3;
 * const backgroundColor = '#ffffff';
 * const action = <Button variant="contained" size="large" color="secondary">Read More</Button>;
 *
 * return (
 *   <BlogCard
 *     image={image}
 *     imageAlt={imageAlt}
 *     title={title}
 *     content={content}
 *     elevation={elevation}
 *     backgroundColor={backgroundColor}
 *     action={action}
 *   />
 * );
 *
 * @param {Object} props - Component props.
 * @param {string} props.image - URL of the image to display.
 * @param {string} props.imageAlt - Alt text for the image.
 * @param {string} props.title - Title of the blog post.
 * @param {string} props.content - Brief description or content of the blog post.
 * @param {number} props.elevation - Elevation level of the card (Material-UI elevation prop).
 * @param {string} props.backgroundColor - Background color of the card.
 * @param {React.ReactNode} props.action - Optional action element to render at the bottom of the card.
 * @returns {JSX.Element} The rendered BlogCard component.
 */
export default function BlogCard({
  image,
  imageAlt,
  title,
  content,
  elevation,
  backgroundColor,
  action,
  padding,
}) {
  const theme = useTheme();
  return (
    <StyledCard
      elevation={elevation}
      backgroundColor={backgroundColor}
      theme={theme}
      padding={padding}
    >
      <CardMedia
        component="img"
        image={image}
        alt={imageAlt}
        sx={{ width: '100%', height: '241px', borderRadius: 2 }}
      />
      <CardContent sx={{ padding: 0, pt: 3 }}>
        <TypographyBlock
          headerVariant="h4"
          paragraphVariant="body2"
          headerFontFamily="heading.fontFamily.primary"
          paragraphFontFamily="body.fontFamily.primary"
          headerChildren={title}
          paragraphChildren={content}
          headerColor="primary"
          paragraphColor="text.primary"
          headerFontWeight={700}
          paragraphFontWeight={400}
          sx={{
              fontSize: '1.5rem',
          }}
        />
        {action}
      </CardContent>
    </StyledCard>
  );
}

BlogCard.propTypes = blogCardPropTypes;
BlogCard.defaultPropss = blogCardDefaultProps;
